import api from './api';

/**
 * Sleep helper for retry backoff.
 */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * POST a single chunk with automatic retry.
 * Flaky Wi-Fi loses one chunk mid-upload → we retry that chunk up to
 * MAX_RETRIES times with exponential backoff instead of aborting the
 * whole multi-minute upload session.
 */
async function postChunkWithRetry(uploadId, index, chunk, opts = {}) {
  const MAX_RETRIES = 4;               // 1 attempt + 3 retries
  const BACKOFF_MS = [750, 2000, 4500]; // between attempts
  let lastErr;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const fd = new FormData();
      fd.append('file', chunk, `chunk_${index}`);
      const res = await api.post(
        `/chunked-upload/${uploadId}/chunk/${index}`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
          // Let the caller observe upload progress for this chunk.
          onUploadProgress: opts.onUploadProgress,
        }
      );
      return res.data;
    } catch (err) {
      lastErr = err;
      // Don't retry on 4xx that isn't a network/timeout issue (e.g. 404
      // "upload session not found" won't recover with a retry).
      const status = err.response?.status;
      const retryable =
        !err.response ||                       // network error / abort
        status === 408 || status === 429 ||    // request timeout / rate-limit
        (status >= 500 && status <= 599);      // server-side hiccup
      if (!retryable || attempt === MAX_RETRIES - 1) throw err;
      await sleep(BACKOFF_MS[attempt] || 5000);
    }
  }
  throw lastErr;
}

/**
 * Upload a large file using chunked uploads.
 * The chunk size MUST match what the backend reports in the init response —
 * otherwise the server's assembly will read truncated chunks and produce
 * corrupted output.
 *
 * @param {File}     file        The browser File object.
 * @param {string}   targetType  "production_visual" | "production_final_asset"
 *                                | "creative_final" | "creative_visual" | "influencer_video"
 *                                | "e2e_cloner_video" | "etsy_product_media"
 * @param {string}   targetId    Parent record id.
 * @param {Function} [onProgress] (percent) => void
 * @param {Object}   [metadata]  Extra hints sent to /init
 */
export async function chunkedUpload(file, targetType, targetId, onProgress, metadata) {
  // Step 1: Init — server tells us how many chunks and the chunk size.
  const initRes = await api.post('/chunked-upload/init', {
    filename: file.name,
    file_size: file.size,
    content_type: file.type || 'application/octet-stream',
    target_type: targetType,
    target_id: targetId,
    metadata: metadata || null,
  });

  const { upload_id, total_chunks, chunk_size } = initRes.data;
  // CRITICAL: use the server-provided chunk_size. Hard-coding a different
  // value here will break assembly (chunk count and offsets will not align).
  const cs = chunk_size || 5 * 1024 * 1024;

  if (onProgress) onProgress(0);

  // Step 2: Upload chunks in order — with per-chunk retry so a single
  // flaky Wi-Fi drop doesn't torch a 5-minute upload session.
  for (let i = 0; i < total_chunks; i++) {
    const start = i * cs;
    const end = Math.min(start + cs, file.size);
    const chunk = file.slice(start, end);

    await postChunkWithRetry(upload_id, i, chunk, {
      onUploadProgress: (evt) => {
        if (!onProgress) return;
        // 0–90% is chunk transfer; 90–100% is /complete.
        const chunkFrac = evt.total ? evt.loaded / evt.total : 0;
        const overall = ((i + chunkFrac) / total_chunks) * 90;
        onProgress(Math.min(90, Math.round(overall)));
      },
    });
    if (onProgress) onProgress(Math.round(((i + 1) / total_chunks) * 90));
  }

  // Step 3: Complete — server assembles chunks and streams to R2.
  const result = await api.post(
    `/chunked-upload/${upload_id}/complete`,
    {},
    { timeout: 300000 }
  );
  if (onProgress) onProgress(100);
  return result.data;
}

/** Files larger than 5 MB go through chunked upload. */
export function shouldUseChunkedUpload(file) {
  return file.size > 5 * 1024 * 1024;
}
